import { todoRepository } from '../db/database';
import {
  TODO_MAX_NOTIFICATIONS,
  TODO_TITLE_MAX_LENGTH,
  type TodoItem,
} from '../models/types';
import { queueNotificationSync } from '../services/notificationSync';
import { todayIso } from '../utils/date';
import { setDelegatedClick } from '../ui/delegatedClick';
import { renderCheckIcon, renderPlusIcon } from '../ui/icons';

/**
 * Yapılacaklar ekranı (Faz 14).
 *
 * ÖNEMLİ — Bu ekran PUANLAMAYA DOKUNMAZ. Yapılacaklar bir alışkanlık
 * değildir: §7.2'deki Başarı Barı formülüne girmez ve §7.3'teki %50
 * hesabının paydasını değiştirmez. Aksi halde listeye bir madde
 * eklemek, o günün ceza tabanını sessizce yükseltirdi — kullanıcı
 * "bir şey ekledim, puanım düştü" durumuyla karşılaşırdı.
 *
 * Ekran iki hâlden birinde bulunur: liste ya da düzenleme formu.
 * Yeni madde EKLEMEK için forma geçilmez — listenin üstündeki hızlı
 * ekleme satırı kullanılır; tek alanlı bir kayıt için tam ekran form
 * gereksiz yere yavaş olurdu.
 */

/** Ekranın o anki hâli. */
type ScreenMode =
  | { type: 'list' }
  /** Var olan bir maddenin başlığını düzenleme. */
  | { type: 'form'; item: TodoItem };

let mode: ScreenMode = { type: 'list' };

/** Silme onayı bekleyen maddenin id'si. */
let pendingDeleteId: string | null = null;

function escapeHtml(text: string): string {
  const element = document.createElement('span');
  element.textContent = text;
  return element.innerHTML;
}

/* ==================================================================
   Liste
   ================================================================== */

/** Hızlı ekleme satırı — listenin en üstünde, her zaman görünür. */
function renderQuickAdd(): string {
  return `
    <!--
      novalidate: tarayıcının kendi doğrulama balonları iOS Safari'de
      çoğu zaman görünmüyor ama formu yine de engelliyor; kullanıcı
      butona basıp hiçbir şey olmadığını görüyordu. Boş başlık
      kontrolünü kendimiz yapıyoruz.
    -->
    <form class="todo-add" data-add-form novalidate>
      <input class="todo-add__input" type="text" name="title"
             maxlength="${TODO_TITLE_MAX_LENGTH}" autocomplete="off"
             placeholder="Yeni bir iş yaz..." aria-label="Yeni yapılacak" />
      <button type="submit" class="todo-add__button" aria-label="Ekle">
        ${renderPlusIcon(18)}
      </button>
    </form>
  `;
}

/** Açık (tamamlanmamış) madde satırı. */
function renderOpenRow(item: TodoItem): string {
  return `
    <li class="todo-row">
      <button type="button" class="todo-row__check" data-complete="${item.id}"
              aria-label="${escapeHtml(item.title)} tamamlandı olarak işaretle"></button>
      <button type="button" class="todo-row__title" data-edit="${item.id}">
        ${escapeHtml(item.title)}
        ${
          // Hatırlatması olan maddeyi listede belli ediyoruz; ayarın
          // varlığını görmek için formu açmak gerekmesin.
          item.notificationsEnabled && item.notificationTimes.length > 0
            ? `<span class="todo-row__times">${item.notificationTimes.join(' · ')}</span>`
            : ''
        }
      </button>
      <button type="button" class="todo-row__delete" data-delete="${item.id}"
              aria-label="${escapeHtml(item.title)} maddesini sil">Sil</button>
    </li>
  `;
}

/** Tamamlanmış madde satırı — üstü çizili, geri alınabilir. */
function renderDoneRow(item: TodoItem): string {
  return `
    <li class="todo-row todo-row--done">
      <button type="button" class="todo-row__check todo-row__check--done"
              data-reopen="${item.id}"
              aria-label="${escapeHtml(item.title)} maddesini geri al">
        ${renderCheckIcon(14)}
      </button>
      <span class="todo-row__title todo-row__title--done">${escapeHtml(item.title)}</span>
      <button type="button" class="todo-row__delete" data-delete="${item.id}"
              aria-label="${escapeHtml(item.title)} maddesini sil">Sil</button>
    </li>
  `;
}

/**
 * Silme onayı.
 *
 * Yapılacak maddesinin geri alınabilir bir arşivi yok (alışkanlıklarda
 * olduğu gibi), o yüzden yanlış bir dokunuş yazdığın metni tamamen
 * kaybettirirdi. Bu yüzden tek dokunuşla silinmiyor.
 */
function renderDeleteConfirm(item: TodoItem): string {
  return `
    <div class="challenge-confirm">
      <p class="challenge-confirm__title">“${escapeHtml(item.title)}” silinsin mi?</p>
      <p class="challenge-confirm__text">Bu madde tamamen kaldırılır, geri alınamaz.</p>
      <div class="challenge-confirm__actions">
        <button type="button" class="challenge-confirm__no" data-abort-delete>Vazgeç</button>
        <button type="button" class="challenge-confirm__yes" data-confirm-delete="${item.id}">
          Evet, sil
        </button>
      </div>
    </div>
  `;
}

function renderList(open: TodoItem[], done: TodoItem[]): string {
  const pending = [...open, ...done].find((item) => item.id === pendingDeleteId);

  const openList = open.length
    ? `<ul class="todo-list" role="list">${open.map(renderOpenRow).join('')}</ul>`
    : `<div class="placeholder">
         <p class="placeholder__title">Liste boş</p>
         <p class="placeholder__hint">Yukarıdaki satıra yazıp ilk işini ekle.</p>
       </div>`;

  const doneSection = done.length
    ? `<h2 class="section-title section-title--spaced">Tamamlananlar</h2>
       <ul class="todo-list" role="list">${done.map(renderDoneRow).join('')}</ul>`
    : '';

  return `
    <h1 class="page-title">Yapılacaklar</h1>
    <p class="page-subtitle">${open.length} açık iş</p>
    ${renderQuickAdd()}
    ${openList}
    ${pending ? renderDeleteConfirm(pending) : ''}
    ${doneSection}
  `;
}

/* ==================================================================
   Düzenleme formu
   ================================================================== */

/** Saat sayısını "HH:MM" biçimine çevirir. */
function formatHour(hour: number, minute = '00'): string {
  return `${String(hour).padStart(2, '0')}:${minute}`;
}

/**
 * Bir sonraki hatırlatma saati için makul bir varsayılan üretir.
 *
 * İki şeyi birden sağlamak zorunda:
 * - Saatler birbirinden ayrık olmalı. Aynı saate düşen hatırlatmalar
 *   tek bildirimde toplandığı için (§7.8.4), yinelenen bir öneri
 *   kullanıcıya beklediğinden az bildirim verirdi — o yüzden kaydetme
 *   sırasında da reddediliyor.
 * - Öneri tahmin edilebilir olmalı: normalde son saatin 3 saat
 *   sonrası.
 *
 * Gün sonuna gelindiğinde (10 bildirime kadar çıkılabiliyor) 3'er
 * saatlik adım yetmez; o noktada boş kalan ilk tam saate düşüyoruz.
 */
function suggestNextTime(existing: string[]): string {
  const used = new Set(existing);
  const last = existing.at(-1);

  if (last) {
    const [hourText = '9', minuteText = '00'] = last.split(':');
    const preferred = formatHour(Math.min(Number(hourText) + 3, 23), minuteText);
    if (!used.has(preferred)) return preferred;
  }

  // Önce uyanık saatler (08–23), sonra gece. 24 tam saat, en fazla 10
  // bildirim için fazlasıyla yeterli.
  for (const hour of [...Array(16).keys()].map((i) => i + 8)) {
    if (!used.has(formatHour(hour))) return formatHour(hour);
  }
  for (const hour of [...Array(8).keys()]) {
    if (!used.has(formatHour(hour))) return formatHour(hour);
  }

  return '09:00';
}

/** Saat girişlerinin listesi — adet değiştikçe yeniden çizilir. */
function renderTimeInputs(times: string[]): string {
  return times
    .map(
      (time, index) => `
        <label class="todo-time">
          <span class="todo-time__index">${index + 1}.</span>
          <input class="todo-time__input" type="time" name="time-${index}"
                 value="${time}" aria-label="${index + 1}. bildirim saati" />
        </label>
      `,
    )
    .join('');
}

/**
 * Bildirim bölümü.
 *
 * §7.8.4 gereği ayar HER MADDE İÇİN AYRIDIR: anahtar, günde kaç
 * bildirim ve o sayı kadar saat. Bildirimler her gün tekrar eder.
 */
function renderNotificationSection(item: TodoItem): string {
  const enabled = item.notificationsEnabled;
  const times = item.notificationTimes.length
    ? item.notificationTimes
    : ['09:00'];

  const countOptions = Array.from({ length: TODO_MAX_NOTIFICATIONS }, (_, index) => {
    const count = index + 1;
    return `<option value="${count}" ${count === times.length ? 'selected' : ''}>
              ${count}
            </option>`;
  }).join('');

  return `
    <div class="field">
      <label class="todo-notify__switch">
        <input type="checkbox" name="notify" data-notify-toggle
               ${enabled ? 'checked' : ''} />
        <span>Her gün hatırlat</span>
      </label>
    </div>

    <div class="todo-notify" data-notify-section ${enabled ? '' : 'hidden'}>
      <label class="field">
        <span class="field__label">Günde kaç bildirim?</span>
        <select class="field__input" name="notifyCount" data-notify-count>
          ${countOptions}
        </select>
      </label>

      <div class="field">
        <span class="field__label">Saatler</span>
        <div class="todo-times" data-times>${renderTimeInputs(times)}</div>
      </div>
    </div>
  `;
}

function renderForm(item: TodoItem): string {
  return `
    <header class="detail-header">
      <button type="button" class="back-button" data-back-to-list aria-label="Listeye dön">
        <svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true" focusable="false">
          <path d="M7 1L1 7l6 6" stroke="currentColor" stroke-width="2" fill="none"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <h1 class="detail-header__title">Düzenle</h1>
    </header>

    <form class="challenge-form" data-edit-form novalidate>
      <label class="field">
        <span class="field__label">Yapılacak</span>
        <input class="field__input" type="text" name="title"
               maxlength="${TODO_TITLE_MAX_LENGTH}"
               value="${escapeHtml(item.title)}" />
      </label>

      ${renderNotificationSection(item)}

      <p class="challenge-form__error" data-error hidden></p>
      <button type="submit" class="challenge-form__submit">Kaydet</button>
    </form>
  `;
}

/* ==================================================================
   Veri işlemleri
   ================================================================== */

/**
 * Yeni madde ekler.
 *
 * `sortOrder` olarak mevcut en büyük değerin bir fazlasını veriyoruz;
 * böylece yeni madde listenin sonuna eklenir ve var olan sıralama
 * bozulmaz.
 */
async function addTodo(title: string): Promise<void> {
  const all = await todoRepository.list();
  const highestOrder = all.reduce((max, item) => Math.max(max, item.sortOrder), -1);

  await todoRepository.upsert({
    id: crypto.randomUUID(),
    title,
    completedDate: null,
    createdAt: todayIso(),
    sortOrder: highestOrder + 1,
    // Bildirim varsayılan olarak KAPALI: kullanıcı istemeden
    // hatırlatma almaya başlamamalı. Düzenleme ekranından açılır.
    notificationsEnabled: false,
    notificationTimes: [],
  });
}

/**
 * Maddeyi tamamlandı olarak işaretler ya da işareti geri alır.
 *
 * Tamamlanan madde sunucuya gönderilen listeden düşer, böylece
 * hatırlatması da durur (§7.8.4). Geri alınınca yeniden girer.
 */
async function setCompleted(id: string, completed: boolean): Promise<void> {
  const item = await todoRepository.get(id);
  if (!item) return;
  await todoRepository.upsert({
    ...item,
    completedDate: completed ? todayIso() : null,
  });
  queueNotificationSync();
}

/* ==================================================================
   Çizim ve olaylar
   ================================================================== */

async function draw(container: HTMLElement): Promise<void> {
  const redraw = (): Promise<void> => draw(container);

  // Modül düzeyindeki `mode` dallanma sonrasında daraltılamadığı için
  // yerel bir kopyaya alıyoruz.
  const current = mode;

  if (current.type === 'form') {
    container.innerHTML = renderForm(current.item);
    attachFormHandlers(container, current.item, redraw);
    return;
  }

  const [open, done] = await Promise.all([
    todoRepository.listOpen(),
    todoRepository.listCompleted(),
  ]);

  container.innerHTML = renderList(open, done);
  attachListHandlers(container, redraw);
}

function attachListHandlers(container: HTMLElement, redraw: () => Promise<void>): void {
  // Hızlı ekleme formu her çizimde yeniden oluştuğu için doğrudan
  // kendisine bağlanabilir; birikme riski yok.
  container.querySelector<HTMLFormElement>('[data-add-form]')?.addEventListener(
    'submit',
    (event) => {
      event.preventDefault();
      const input = container.querySelector<HTMLInputElement>('.todo-add__input');
      const title = input?.value.trim() ?? '';
      // Boş ya da yalnızca boşluktan ibaret başlık eklemiyoruz; hata
      // göstermeye de gerek yok, kullanıcı zaten hiçbir şey yazmadı.
      if (!title) return;

      void (async () => {
        await addTodo(title);
        await redraw();
        // Çizimden sonra alan yeniden oluştuğu için yeni referansı
        // buluyoruz; odağı orada bırakınca arka arkaya madde eklemek
        // mümkün oluyor.
        container.querySelector<HTMLInputElement>('.todo-add__input')?.focus();
      })();
    },
  );

  // Liste her işlemden sonra kendini yeniden çiziyor. `setDelegatedClick`
  // bir önceki çizimin dinleyicisini kaldırır — aksi halde dinleyiciler
  // üst üste birikir ve tek dokunuş birden çok kez işlenirdi.
  setDelegatedClick('todo', container, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const complete = target.closest<HTMLElement>('[data-complete]');
    if (complete) {
      void (async () => {
        await setCompleted(complete.dataset['complete'] ?? '', true);
        await redraw();
      })();
      return;
    }

    const reopen = target.closest<HTMLElement>('[data-reopen]');
    if (reopen) {
      void (async () => {
        await setCompleted(reopen.dataset['reopen'] ?? '', false);
        await redraw();
      })();
      return;
    }

    const edit = target.closest<HTMLElement>('[data-edit]');
    if (edit) {
      void (async () => {
        const item = await todoRepository.get(edit.dataset['edit'] ?? '');
        if (item) mode = { type: 'form', item };
        await redraw();
      })();
      return;
    }

    const remove = target.closest<HTMLElement>('[data-delete]');
    if (remove) {
      pendingDeleteId = remove.dataset['delete'] ?? null;
      void redraw();
      return;
    }

    if (target.closest('[data-abort-delete]')) {
      pendingDeleteId = null;
      void redraw();
      return;
    }

    const confirmDelete = target.closest<HTMLElement>('[data-confirm-delete]');
    if (confirmDelete) {
      void (async () => {
        await todoRepository.remove(confirmDelete.dataset['confirmDelete'] ?? '');
        // §7.8.4 — Silinen maddenin sunucudaki kopyası da düşmeli;
        // aksi halde artık var olmayan bir iş için hatırlatma gelirdi.
        queueNotificationSync();
        pendingDeleteId = null;
        await redraw();
      })();
    }
  });
}

/** Formdaki saat kutularını okur. */
function readTimeInputs(form: HTMLFormElement): string[] {
  return [...form.querySelectorAll<HTMLInputElement>('.todo-time__input')].map(
    (input) => input.value,
  );
}

function attachFormHandlers(
  container: HTMLElement,
  item: TodoItem,
  redraw: () => Promise<void>,
): void {
  container.querySelector('[data-back-to-list]')?.addEventListener('click', () => {
    mode = { type: 'list' };
    void redraw();
  });

  const form = container.querySelector<HTMLFormElement>('[data-edit-form]');
  if (!form) return;

  const section = form.querySelector<HTMLElement>('[data-notify-section]');
  const toggle = form.querySelector<HTMLInputElement>('[data-notify-toggle]');
  const countField = form.querySelector<HTMLSelectElement>('[data-notify-count]');
  const timesBox = form.querySelector<HTMLElement>('[data-times]');

  // Anahtar açık/kapalıyken saat ayarlarını göster/gizle. Kapalıyken
  // saatleri silmiyoruz — kullanıcı yanlışlıkla kapatıp tekrar açarsa
  // ayarları kaybetmesin.
  toggle?.addEventListener('change', () => {
    if (section) section.hidden = !toggle.checked;
  });

  // Adet değişince saat kutularını yeniden kur. Mevcut değerler
  // korunuyor; yalnızca eklenen kutulara varsayılan öneriliyor.
  countField?.addEventListener('change', () => {
    if (!timesBox) return;

    const desired = Number(countField.value);
    const current = readTimeInputs(form);
    const next = current.slice(0, desired);
    while (next.length < desired) {
      next.push(suggestNextTime(next));
    }
    timesBox.innerHTML = renderTimeInputs(next);
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const titleInput = form.querySelector<HTMLInputElement>('[name="title"]');
    const title = titleInput?.value.trim() ?? '';
    const error = container.querySelector<HTMLElement>('[data-error]');

    const fail = (message: string): void => {
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    };

    if (!title) {
      fail('Bir şeyler yazman gerekiyor.');
      return;
    }

    const notificationsEnabled = toggle?.checked ?? false;
    const times = notificationsEnabled ? readTimeInputs(form) : item.notificationTimes;

    if (notificationsEnabled) {
      // Boş bırakılmış saat kutusu, sessizce yok sayılırsa kullanıcı
      // kurduğunu sandığı bir hatırlatmayı hiç almazdı.
      if (times.some((time) => !time)) {
        fail('Tüm saatleri doldurman gerekiyor.');
        return;
      }
      // Aynı saat iki kez seçilirse ikisi TEK bildirimde toplanır
      // (§7.8.4) ve kullanıcı seçtiğinden az bildirim alırdı. Sessizce
      // katlamak yerine söylüyoruz.
      if (new Set(times).size !== times.length) {
        fail('Aynı saati birden fazla kez seçemezsin.');
        return;
      }
    }

    void (async () => {
      await todoRepository.upsert({
        ...item,
        title,
        notificationsEnabled,
        // Sıralayarak saklıyoruz: dizinin sırası hiçbir anlam taşımıyor
        // (sunucu saate göre gruplar), ama listede ve bir sonraki
        // düzenlemede kronolojik görünmesi daha okunaklı.
        notificationTimes: [...times].sort(),
      });
      queueNotificationSync();
      mode = { type: 'list' };
      await redraw();
    })();
  });
}

export async function renderTodoPage(container: HTMLElement): Promise<void> {
  await draw(container);
}

/**
 * Ekrandan çıkarken geçici durumu sıfırlar.
 *
 * `mode` ve `pendingDeleteId` modül düzeyinde tutuluyor; temizlenmezse
 * kullanıcı düzenleme formundayken başka bir sekmeye geçip geri
 * döndüğünde ekran yine formda açılırdı.
 */
export function resetTodoScreenState(): void {
  mode = { type: 'list' };
  pendingDeleteId = null;
}
